'use client'

import * as React from 'react'
import { addDoc, collection, getDocs, query, serverTimestamp, where } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Textarea } from 'components/ui/textarea'
import { cn } from 'lib/utils'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useProject } from './project-context'
import { useUser } from 'components/context/user-context'

const statusColors = {
  completed: 'bg-emerald-500',
  in_progress: 'bg-blue-500',
  not_started: 'bg-gray-300',
  delayed: 'bg-red-500',
}

const statusLabels = {
  completed: 'Completed',
  in_progress: 'In Progress',
  not_started: 'Not Started',
  delayed: 'Delayed',
}

const priorityColors = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-emerald-100 text-emerald-800',
}
const AUDIT_COLLECTION = 'auditLogs'

function toDateValue(value) {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

function formatDate(value) {
  const parsed = toDateValue(value)
  if (!parsed) return 'TBD'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function ProjectWorkplanPage() {
  const { project } = useProject()
  const { canEdit } = useUser()
  const [deliverables, setDeliverables] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [users, setUsers] = React.useState([])
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [showMore, setShowMore] = React.useState(false)
  const [selectedPersonId, setSelectedPersonId] = React.useState('')
  const [form, setForm] = React.useState({
    name: '',
    plannedStart: '',
    plannedEnd: '',
    leadPersonId: '',
    leadPersonName: '',
    responsiblePeople: [],
    priority: 'medium',
    status: 'not_started',
    description: '',
    phase: '',
  })

  React.useEffect(() => {
    let isMounted = true

    async function loadDeliverables() {
      try {
        const deliverablesQuery = query(
          collection(db, 'deliverables'),
          where('projectId', '==', project.id),
        )
        const snapshot = await getDocs(deliverablesQuery)
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setDeliverables(records)
      } catch {
        setDeliverables([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadDeliverables()

    return () => {
      isMounted = false
    }
  }, [project.id])

  React.useEffect(() => {
    let isMounted = true

    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setUsers(records)
      } catch {
        setUsers([])
      }
    }

    loadUsers()

    return () => {
      isMounted = false
    }
  }, [])

  const resetForm = () => {
    setForm({
      name: '',
      plannedStart: '',
      plannedEnd: '',
      leadPersonId: '',
      leadPersonName: '',
      responsiblePeople: [],
      priority: 'medium',
      status: 'not_started',
      description: '',
      phase: '',
    })
    setShowMore(false)
    setSelectedPersonId('')
  }

  const handleSaveDeliverable = async () => {
    if (!form.name.trim() || !form.plannedStart || !form.plannedEnd || form.responsiblePeople.length === 0) {
      toast.error('Please fill in the deliverable name, dates, and responsible person(s).')
      return
    }
    if (saving) return
    setSaving(true)
    try {
      const payload = {
        projectId: project.id,
        name: form.name.trim(),
        plannedStart: form.plannedStart,
        plannedEnd: form.plannedEnd,
        leadPersonId: form.leadPersonId,
        leadPersonName: form.leadPersonName,
        responsiblePersonId: form.responsiblePeople[0]?.id || '',
        responsiblePersonName: form.responsiblePeople[0]?.name || '',
        responsiblePersonIds: form.responsiblePeople.map((person) => person.id),
        responsiblePersonNames: form.responsiblePeople.map((person) => person.name),
        priority: form.priority,
        status: form.status,
        description: form.description.trim(),
        phase: form.phase.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      const docRef = await addDoc(collection(db, 'deliverables'), payload)
      setDeliverables((prev) => [{ id: docRef.id, ...payload }, ...prev])
      try {
        await addDoc(collection(db, AUDIT_COLLECTION), {
          timestamp: serverTimestamp(),
          userId: auth.currentUser?.uid || 'system',
          userDisplayName: auth.currentUser?.displayName || auth.currentUser?.email || 'System',
          action: 'CREATE',
          entity: 'Project',
          entityId: project.id,
          changes: {
            before: '',
            after: `Added deliverable "${payload.name}"`,
          },
        })
      } catch (error) {
        console.warn('Failed to write audit log', error)
      }
      toast.success('Deliverable added.')
      resetForm()
      setSheetOpen(false)
    } catch (error) {
      toast.error('Failed to add deliverable', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  const addResponsiblePerson = () => {
    if (!selectedPersonId) return
    const selected = users.find((user) => (user.userId || user.id) === selectedPersonId)
    if (!selected) return
    const personId = selected.userId || selected.id
    const personName = selected.fullName || selected.name || selected.email || 'User'
    setForm((prev) => {
      if (prev.responsiblePeople.some((person) => person.id === personId)) {
        return prev
      }
      return {
        ...prev,
        responsiblePeople: [...prev.responsiblePeople, { id: personId, name: personName }],
      }
    })
    setSelectedPersonId('')
  }

  const removeResponsiblePerson = (personId) => {
    setForm((prev) => ({
      ...prev,
      responsiblePeople: prev.responsiblePeople.filter((person) => person.id !== personId),
    }))
  }

  const projectDeliverables = deliverables

  const allDates = projectDeliverables
    .flatMap((item) =>
      [item.plannedStart, item.plannedEnd]
        .filter(Boolean)
        .map((dt) => toDateValue(dt))
        .filter(Boolean)
        .map((dt) => dt.getTime()),
    )
  const minDate = allDates.length > 0 ? Math.min(...allDates) : Date.now()
  const maxDate = allDates.length > 0 ? Math.max(...allDates) : Date.now()
  const totalDuration = maxDate - minDate || 1

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Gantt Chart</CardTitle>
            <CardDescription>Project timeline and milestone progress</CardDescription>
          </div>
          {canEdit ? (
            <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
              <DialogTrigger asChild>
                <Button size="sm">Add Deliverable</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Deliverable</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Deliverable Name</Label>
                  <Input
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="e.g. Baseline survey completed"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={form.plannedStart}
                      onChange={(event) => setForm((prev) => ({ ...prev, plannedStart: event.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={form.plannedEnd}
                      onChange={(event) => setForm((prev) => ({ ...prev, plannedEnd: event.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Lead Person</Label>
                  <Select
                    value={form.leadPersonId}
                    onValueChange={(value) => {
                      const selected = users.find((user) => (user.userId || user.id) === value)
                      setForm((prev) => ({
                        ...prev,
                        leadPersonId: value,
                        leadPersonName: selected?.fullName || selected?.name || selected?.email || '',
                      }))
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select lead person" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.length === 0 ? (
                        <SelectItem value="no-users" disabled>
                          No users available
                        </SelectItem>
                      ) : (
                        users.map((user) => (
                          <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                            {user.fullName || user.name || user.email || 'User'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Responsible Person</Label>
                  <div className="flex gap-2">
                    <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select person" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.length === 0 ? (
                          <SelectItem value="no-users" disabled>
                            No users available
                          </SelectItem>
                        ) : (
                          users.map((user) => (
                            <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                              {user.fullName || user.name || user.email || 'User'}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="sm" onClick={addResponsiblePerson} disabled={!selectedPersonId}>
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.responsiblePeople.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No responsible persons added.</p>
                    ) : (
                      form.responsiblePeople.map((person) => (
                        <div
                          key={person.id}
                          className="flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-foreground"
                        >
                          <span>{person.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-5"
                            onClick={() => removeResponsiblePerson(person.id)}
                          >
                            x
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <Collapsible open={showMore} onOpenChange={setShowMore}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="px-0">
                      {showMore ? 'Hide details' : 'More details'}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 pt-2">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select
                          value={form.status}
                          onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_started">Not Started</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="delayed">Delayed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select
                          value={form.priority}
                          onValueChange={(value) => setForm((prev) => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Milestone / Phase</Label>
                      <Input
                        value={form.phase}
                        onChange={(event) => setForm((prev) => ({ ...prev, phase: event.target.value }))}
                        placeholder="Phase 1, Planning, Execution"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        rows={3}
                        value={form.description}
                        onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                        placeholder="Optional description or notes"
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <Button className="w-full" onClick={handleSaveDeliverable} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Deliverable'}
                </Button>
              </div>
              </DialogContent>
            </Dialog>
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-48 shrink-0" />
              <div className="flex-1 flex justify-between text-xs text-muted-foreground">
                <span>{new Date(minDate).toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })}</span>
                <span>{new Date((minDate + maxDate) / 2).toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })}</span>
                <span>{new Date(maxDate).toLocaleDateString('en-KE', { month: 'short', year: '2-digit' })}</span>
              </div>
            </div>

            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading deliverables...</div>
            ) : projectDeliverables.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No deliverables added yet.</div>
            ) : (
              projectDeliverables.map((item) => {
                const start = toDateValue(item.plannedStart)
                const end = toDateValue(item.plannedEnd)
                const startTime = start ? start.getTime() : minDate
                const endTime = end ? end.getTime() : minDate
                const leftPct = ((startTime - minDate) / totalDuration) * 100
                const widthPct = ((endTime - startTime) / totalDuration) * 100
                const status = item.status || 'not_started'
                return (
                  <div key={item.id} className="flex items-center gap-3 py-1.5">
                    <div className="w-48 shrink-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.name || 'Deliverable'}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.leadPersonName ? `Lead: ${item.leadPersonName}` : 'Lead: Unassigned'}
                        {item.responsiblePersonNames && item.responsiblePersonNames.length > 0
                          ? ` · Resp: ${item.responsiblePersonNames.join(', ')}`
                          : item.responsiblePersonName
                            ? ` · Resp: ${item.responsiblePersonName}`
                            : ''}
                      </p>
                    </div>
                    <div className="flex-1 relative h-7 rounded bg-muted/50">
                      <div
                        className={cn('absolute top-1 h-5 rounded transition-all', statusColors[status] || statusColors.not_started)}
                        style={{
                          left: `${Math.max(0, leftPct)}%`,
                          width: `${Math.max(2, widthPct)}%`,
                          opacity: status === 'not_started' ? 0.4 : 0.85,
                        }}
                      />
                    </div>
                    <Badge variant="secondary" className="text-[10px]">
                      {statusLabels[status] || status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                )
              })
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Task Board</CardTitle>
          <CardDescription>Drag tasks between columns to update status</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading tasks...</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {['not_started', 'in_progress', 'completed', 'delayed'].map((status) => {
                const tasks = projectDeliverables.filter((item) => item.status === status)
                return (
                  <div key={status} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-foreground">{statusLabels[status]}</h4>
                      <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
                    </div>
                    <div className="space-y-2 min-h-24">
                      {tasks.map((task) => (
                        <div key={task.id} className="rounded-lg border bg-card p-3 hover:shadow-sm transition-shadow">
                          <p className="text-sm font-medium text-foreground">{task.name || 'Task'}</p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1">
                              <div className="size-5 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-bold text-primary">
                              {((task.leadPersonName) || (task.responsiblePersonNames && task.responsiblePersonNames[0]) || task.responsiblePersonName || 'U')
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Badge
                                variant="secondary"
                                className={cn('text-[10px] px-1.5 py-0 border-0', priorityColors[task.priority] || priorityColors.medium)}
                              >
                                {task.priority || 'medium'}
                              </Badge>
                              <span className="text-[10px] text-muted-foreground">{formatDate(task.plannedEnd)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {tasks.length === 0 && (
                        <div className="rounded-lg border border-dashed p-4 text-center">
                          <p className="text-xs text-muted-foreground">No tasks</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
